/**
 * FoundationPanel — shared GL(n,ℂ) realization + honest mechanization
 * (RD-56 + RD-65).
 *
 * Two parts. (a) The @csk/core GL(n,ℂ) realization the whole app runs on — the
 * TS realization of RD-98 — with the operations both faces use. (b) The honest
 * mechanization status for BOTH RDs, read from the local `data/theorems-mit.json`
 * and `data/theorems-vid.json`: RD-56 (MIT) is Research-Stable with
 * ConcreteGL2Instance.agda pending; RD-65 abstract VID is proven (zero
 * postulates, --safe --without-K) with the parametric GL(n) obligations open
 * for all n. Mechanization here is pointers, not transcribed proofs.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */
import React from "react";
import type { MonographDataTableColumn } from "@csk/demo-ui";
import { AgdaSourceLink, PanelSurface, panelTokens } from "@csk/demo-ui";
import mitData from "../data/theorems-mit.json" with { type: "json" };
import vidData from "../data/theorems-vid.json" with { type: "json" };
type MitRow = (typeof mitData.theorems)[number];
function mitStatusColor(status: string): string {
  if (status === "Proved" || status.startsWith("Proved"))
    return panelTokens.immuneGreen;
  if (status === "Structurally discharged") return panelTokens.accentCyan;
  return panelTokens.accentYellow;
}
const MIT_COLUMNS: readonly MonographDataTableColumn<MitRow>[] = [
  {
    accessorKey: "id",
    header: "Code",
    cell: ({ row }) => (
      <span className="font-mono text-[12.5px] font-bold text-[var(--mit-bright)]">
        {row.original.id}
      </span>
    ),
  },
  {
    accessorKey: "name",
    header: "Sub-theorem",
    cell: ({ row }) => (
      <span className="text-[12.5px] text-[var(--mit-text)]">
        {row.original.name}
      </span>
    ),
  },
  {
    accessorKey: "status",
    header: "Status",
    cell: ({ row }) =>
      PanelSurface.badge(
        row.original.status,
        mitStatusColor(row.original.status),
      ),
  },
  {
    accessorKey: "agdaFile",
    header: "Agda module",
    cell: ({ row }) => <AgdaSourceLink module={row.original.agdaFile} />,
  },
];
type MonographStatusTone =
  | "complete"
  | "queued"
  | "planned"
  | "warning"
  | "blocked"
  | "neutral";
function vidStatusTone(status: string): MonographStatusTone {
  if (status === "PROVED") return "complete";
  if (status === "DEFINED") return "neutral";
  return "queued";
}
export function FoundationPanel(): React.JSX.Element {
  return (
    <PanelSurface.PanelShell
      title="Foundation & Mechanization"
      subtitle="The shared @csk/core GL(n,ℂ) realization both faces run on, plus the honest mechanization status for RD-56 and RD-65. Pointers, not transcribed proofs."
      accentColor={panelTokens.accentTeal}
    >
      <PanelSurface.SectionHeading>
        The shared GL(n,ℂ) realization
      </PanelSurface.SectionHeading>
      <PanelSurface.Callout
        title="Both faces run on the real @csk/core GL(n,ℂ) realization"
        tone="safe"
        color={panelTokens.accentTeal}
      >
        <PanelSurface.Prose className="m-0">
          The demo does not stub the matrix algebra. Every generator and every
          audited matrix is a real gl(n,ℂ) element{" "}
          <code className="font-mono">ComplexMatrix</code>, and each condition
          is computed live on the @csk/core Mₙ(ℂ) realization (
          <AgdaSourceLink module="realizations/typescript/src/research/gl2c-tangent-embedding.ts" />
          , the TS realization of RD-98). A1 forms{" "}
          <code className="font-mono">exp(X)</code> and applies a computable
          authority-subgroup membership predicate; A2 reads the n eigenvalues
          (which always exist over ℂ); A3 uses the exp semigroup law; A4
          evaluates the Jacobi residual{" "}
          <code className="font-mono">
            |cmatDet(cmatExp X) − cExp(cmatTrace X)|
          </code>
          , which is zero within tolerance for every generator. The realization
          is NUMERICAL (floating-point verification of the algebraic
          identities); the Agda anchors below remain the normative proof path.
        </PanelSurface.Prose>
      </PanelSurface.Callout>

      <PanelSurface.SectionHeading>Operations used</PanelSurface.SectionHeading>
      <PanelSurface.Card>
        <PanelSurface.FactList
          ariaLabel="GL(n,ℂ) realization operations"
          items={[
            {
              id: "cmatDet",
              label: "cmatDet",
              value: "Determinant of a ComplexMatrix — A4 resource accounting.",
            },
            {
              id: "cmatTrace",
              label: "cmatTrace",
              value:
                "Trace of a ComplexMatrix — A3 temporal trace, A4 master identity.",
            },
            {
              id: "cmatExp",
              label: "cmatExp",
              value:
                "Matrix exponential exp(X) — A1 membership, A3 flow, A4 residual.",
            },
            {
              id: "cmatEigenvalues",
              label: "cmatEigenvalues",
              value: "The n eigenvalues over ℂ — A2 spectral separation.",
            },
            {
              id: "cExp",
              label: "cExp",
              value: "Complex exponential — exp(Tr X) in the Jacobi identity.",
            },
            {
              id: "cMod",
              label: "cMod",
              value: "Complex modulus — tolerance comparisons on residuals.",
            },
          ]}
        />
      </PanelSurface.Card>

      <PanelSurface.SectionHeading>
        RD-56 · Matrix Immunity Theorem — mechanization
      </PanelSurface.SectionHeading>
      <PanelSurface.Card>
        <PanelSurface.Prose className="m-0">
          The Matrix Immunity Theorem is{" "}
          <strong className="text-[var(--mit-bright)]">proved</strong>; what
          follows are pointers into the Agda development. MIT itself is{" "}
          <em>structurally discharged</em>; the supporting sub-theorems are
          proved or structural exactly as RD-56 records them.
        </PanelSurface.Prose>
      </PanelSurface.Card>
      <PanelSurface.Card>
        <PanelSurface.DataTable
          ariaLabel="RD-56 proof status and Agda location"
          data={mitData.theorems}
          columns={MIT_COLUMNS}
          getRowId={(row) => row.id}
        />
      </PanelSurface.Card>
      <PanelSurface.Callout
        title={`${mitData.graduation.tier} — ${mitData.graduation.summary}`}
        tone="warn"
        color={panelTokens.accentGold}
      >
        {mitData.graduation.nextGate}
      </PanelSurface.Callout>
      <PanelSurface.Card>
        <PanelSurface.FactList
          ariaLabel="RD-56 naming triad"
          items={[
            {
              id: "mit-security",
              label: "Security",
              value: mitData.namingTriad.security,
            },
            {
              id: "mit-governance",
              label: "Governance",
              value: mitData.namingTriad.governance,
            },
            {
              id: "mit-mathematical",
              label: "Mathematical",
              value: mitData.namingTriad.mathematical,
            },
          ]}
        />
      </PanelSurface.Card>

      <PanelSurface.SectionHeading>
        RD-65 · Vulnerability Immunity Distillation — mechanization
      </PanelSurface.SectionHeading>
      <PanelSurface.Card>
        <PanelSurface.BadgeRail
          items={[
            {
              id: "vid-security",
              label: `Security · ${vidData.namingTriad.security}`,
              color: panelTokens.accent,
            },
            {
              id: "vid-governance",
              label: `Governance · ${vidData.namingTriad.governance}`,
              color: panelTokens.carrier,
            },
            {
              id: "vid-mathematical",
              label: `Mathematical · ${vidData.namingTriad.mathematical}`,
              color: panelTokens.immune,
            },
          ]}
        />
      </PanelSurface.Card>
      <PanelSurface.Card>
        <PanelSurface.StatusList
          ariaLabel="Vulnerability Immunity Distillation records"
          items={vidData.theorems.map((t) => ({
            id: t.id,
            title: t.label,
            status: t.status,
            tone: vidStatusTone(t.status),
            color:
              t.status === "PROVED" ? panelTokens.immune : panelTokens.carrier,
            description: t.statement,
            code: t.agdaName,
            meta: t.role,
            path: t.agdaFile,
          }))}
        />
      </PanelSurface.Card>
      <PanelSurface.Callout
        title="Abstract VID: fully proven, zero postulates"
        tone="safe"
        color={panelTokens.immune}
      >
        <PanelSurface.Prose className="m-0">
          The constructive Agda mechanization is complete in{" "}
          <AgdaSourceLink module="agda/AttackSurfaceConvergence/ImmunityDistillation.agda" />
          . <code className="font-mono">VulnerabilityImmunityDistillation</code>{" "}
          packages convergence (via the abstract ASCT), the MIT certificate (via{" "}
          <code className="font-mono">build-immunity</code>), and the least
          fixed point (via <code className="font-mono">immune-contained</code>).
          Termination is witnessed by{" "}
          <code className="font-mono">Acc _&lt;_</code> on the generator count.
          Module qualifier:{" "}
          <code className="font-mono">--safe --without-K</code>. No postulates.
        </PanelSurface.Prose>
      </PanelSurface.Callout>
      <PanelSurface.Callout
        title="Parametric GL(n) realization: open module parameters for all n"
        tone="warn"
        color={panelTokens.accentGold}
      >
        <PanelSurface.Prose className="m-0">
          The pipeline is realized parametrically in <em>n</em> by{" "}
          <AgdaSourceLink module="agda/MatrixKernel/ImmunityDistillationOverMatrices.agda" />
          , which instantiates the abstract record over arbitrary GL(n). Four
          obligations the per-instance{" "}
          <code className="font-mono">MatrixImmunityPattern</code> modules do
          not yet supply universally — matrix exponential, A4 trace resource,
          decidable axiom checks, and exp-bracket compatibility, each for
          arbitrary <em>n</em> — are threaded as{" "}
          <strong className="text-[var(--vid-bright)]">
            module parameters
          </strong>{" "}
          (zero new postulates). Discharging each for all <em>n</em> is{" "}
          <strong className="text-[var(--vid-bright)]">not yet complete</strong>{" "}
          and is tracked by the open GitHub issue “MatrixKernel: discharge VID
          open obligations for all n.”
        </PanelSurface.Prose>
        <div className="mt-2.5 flex flex-wrap gap-1.5">
          {PanelSurface.badge("matrix exponential ∀n", panelTokens.accentGold)}
          {PanelSurface.badge("A4 trace resource ∀n", panelTokens.accentGold)}
          {PanelSurface.badge(
            "decidable axiom checks ∀n",
            panelTokens.accentGold,
          )}
          {PanelSurface.badge("exp-bracket compat ∀n", panelTokens.accentGold)}
        </div>
      </PanelSurface.Callout>

      <PanelSurface.RepositoryLinks
        items={[
          {
            label: "GL(2,ℂ) realization (@csk/core, RD-98)",
            path: "realizations/typescript/src/research/gl2c-tangent-embedding.ts",
          },
          {
            label: "RD-56 §Proof status and Agda location",
            path: "research/settled/RD-56-MATRIX-IMMUNITY-THEOREM.md",
          },
          {
            label: "UniversalModelProof.agda (MIT)",
            path: "agda/Research/SecureGovernancePatternGL/UniversalModelProof.agda",
          },
          {
            label: "VID over abstract Mat",
            path: "agda/AttackSurfaceConvergence/ImmunityDistillation.agda",
          },
          {
            label: "Parametric GL(n) realization",
            path: "agda/MatrixKernel/ImmunityDistillationOverMatrices.agda",
          },
          {
            label: "RD-65 manifest",
            path: "research/settled/RD-65-IMMUNITY-DISTILLATION.md",
          },
        ]}
      />
    </PanelSurface.PanelShell>
  );
}
